import { useState, useRef, useEffect, memo } from "react";
import { motion } from "framer-motion";
import { 
  Send, 
  Wallet, 
  PiggyBank, 
  TrendingUp, 
  Store, 
  ArrowRight,
  MapPin,
  ExternalLink,
  HandCoins,
  Banknote,
  Heart,
  Briefcase,
  ThumbsUp,
  ThumbsDown,
  LogOut,
  MessageSquarePlus,
  Download,
  RotateCcw,
  Globe
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { ChatContent } from "../components/ChatContent";

import { SurveyModal } from "../components/chat/SurveyModal";
import { useHealthCheck } from "../hooks/useHealthCheck";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Brand Colors
const COLORS = {
  primaryDark: "#08263e",
  primaryRed: "#ec474e",
  secondaryBlue: "#518dca",
  secondaryLight: "#f1f2f2",
};

// Cebuana Lhuillier Links
const CEBUANA_LINKS = {
  branchLocator: "https://www.cebuanalhuillier.com/branch-locator/",
  microSavings: "https://www.cebuanalhuillier.com/microsavings/",
  pawn: "https://www.cebuanalhuillier.com/pawn/",
  loans: "https://www.cebuanalhuillier.com/loans/",
  insurance: "https://www.cebuanalhuillier.com/microinsurance/",
  peraPadala: "https://www.cebuanalhuillier.com/pera-padala/",
  kanegosyo: "https://kanegosyocenter.com/",
};

// Quick-start financial advice cards — localized prompts per language
const QUICK_START_CARDS = [
  {
    id: "save",
    icon: PiggyBank,
    title: { default: "I want to save money", english: "I want to save money" },
    subtitle: { default: "Paano mag-ipon", english: "Practical saving tips" },
    prompt: {
      default: "Gusto ko matutong mag-ipon ng pera. Ano ang practical tips mo?",
      english: "I want to learn how to save money. What are your practical tips?",
      bisaya: "Gusto ko makakat-on mag-ipon og kwarta. Unsa imong practical tips?",
      ilokano: "Kayat ko matukod ag-ipon ti kuarta. Ania dagiti practical tips mo?",
      bikolano: "Gusto ko makanood mag-ipon nin kwarta. Ano an practical tips mo?",
    },
    color: "#22c55e",
  },
  {
    id: "cash",
    icon: HandCoins,
    title: { default: "I need quick cash", english: "I need quick cash" },
    subtitle: { default: "Kailangan ko ng pera agad", english: "Quick cash options" },
    prompt: {
      default: "Kailangan ko ng pera agad. Ano ang mga options ko?",
      english: "I need cash right now. What are my options?",
      bisaya: "Kinahanglan nako og kwarta karon dayon. Unsa akong mga options?",
      ilokano: "Masapul ko ti kuarta a dagus. Ania dagiti options ko?",
      bikolano: "Kaipuhan ko nin kwarta tulos. Ano an mga options ko?",
    },
    color: "#f59e0b",
  },
  {
    id: "debt",
    icon: Banknote,
    title: { default: "Help me with my debt", english: "Help me with my debt" },
    subtitle: { default: "Tulong sa utang ko", english: "Debt payoff planning" },
    prompt: {
      default: "May utang ako at gusto ko mag-plano kung paano bayaran. Paano?",
      english: "I have debt and I want to plan how to pay it off. How do I start?",
      bisaya: "Naa koy utang ug gusto nako mag-plano unsaon pagbayad. Unsaon?",
      ilokano: "Adda utang ko ken kayat ko ti plano tapno mabayadan. Kasano?",
      bikolano: "Igwa akong utang asin gusto ko mag-plano kun paano babayaran. Paano?",
    },
    color: "#ec474e",
  },
  {
    id: "business",
    icon: Briefcase,
    title: { default: "I want to start a business", english: "I want to start a business" },
    subtitle: { default: "Gusto ko mag-negosyo", english: "Starting a business" },
    prompt: {
      default: "Gusto ko mag-start ng sarili kong negosyo. Saan ako magsisimula?",
      english: "I want to start my own business. Where do I begin?",
      bisaya: "Gusto nako mag-start og sarili nakong negosyo. Asa ko mag-sugod?",
      ilokano: "Kayat ko ti mangrugi ti bukod ko a negosyo. Sadino ti pangrugian ko?",
      bikolano: "Gusto ko mag-start nin sadiri kong negosyo. Sain ako mag-puon?",
    },
    color: "#8b5cf6",
  },
  {
    id: "budget",
    icon: Wallet,
    title: { default: "Help me budget", english: "Help me budget" },
    subtitle: { default: "Paano mag-budget", english: "Budgeting my income" },
    prompt: {
      default: "Lagi nauubos ang sweldo ko. Paano ba mag-budget ng maayos?",
      english: "My salary always runs out. How do I budget properly?",
      bisaya: "Kanunay ra mahurot akong sweldo. Unsaon pag-budget og maayo?",
      ilokano: "Kanayon a maubos ti sweldo ko. Kasano ti umiso a pag-budget?",
      bikolano: "Pirmi naubos an sweldo ko. Paano mag-budget nin tama?",
    },
    color: "#3b82f6",
  },
  {
    id: "protect",
    icon: Heart,
    title: { default: "Protect my family", english: "Protect my family" },
    subtitle: { default: "Insurance at seguro", english: "Affordable insurance" },
    prompt: {
      default: "Ano ang affordable na insurance para protektahan ang pamilya ko?",
      english: "What affordable insurance options can protect my family?",
      bisaya: "Unsa ang affordable nga insurance para mapanalipdan ang akong pamilya?",
      ilokano: "Ania ti affordable nga insurance tapno masalakniban ti pamilya ko?",
      bikolano: "Ano an affordable na insurance para maprotektahan an pamilya ko?",
    },
    color: "#ec4899",
  },
];

// Helper: pick localized text from a card field
const localize = (field, lang) => {
  if (typeof field === "string") return field;
  return field[lang] || field["default"] || Object.values(field)[0];
};

// Language options
const LANGUAGE_OPTIONS = [
  { id: "taglish", label: "Taglish", flag: "🇵🇭" },
  { id: "english", label: "English", flag: "🇺🇸" },
  { id: "tagalog", label: "Tagalog", flag: "🇵🇭" },
  { id: "bisaya", label: "Bisaya", flag: "🇵🇭" },
  { id: "ilokano", label: "Ilokano", flag: "🇵🇭" },
  { id: "bikolano", label: "Bikolano", flag: "🇵🇭" },
];

// Common concern shortcuts (shown after bot replies)
const COMMON_SHORTCUTS = [
  "How can I reduce expenses?",
  "Paano mag-start ng emergency fund?",
  "Ano ang best way to pay off debt?",
  "Can you help me make a budget?",
  "What Cebuana services can help me?",
];

// Memoized Avatar Component
const MoneyGuroAvatar = memo(({ size = 40, className = "" }) => (
  <div 
    className={`flex items-center justify-center rounded-full overflow-hidden flex-shrink-0 ${className}`}
    style={{ width: size, height: size }}
  >
    <img
      src="/moneyguro-logo.png"
      alt="Money Guro"
      style={{ width: size, height: size, objectFit: 'contain' }}
    />
  </div>
));

// Typing indicator
const TypingIndicator = memo(() => (
  <div className="flex items-start gap-2 sm:gap-3 mb-4">
    <MoneyGuroAvatar size={32} className="border-2" style={{ borderColor: COLORS.primaryRed }} />
    <div 
      className="text-white rounded-2xl rounded-tl-none flex items-center gap-1 py-3 px-4"
      style={{ backgroundColor: COLORS.primaryDark }}
    >
      <div className="w-2 h-2 bg-white/70 rounded-full typing-dot"></div>
      <div className="w-2 h-2 bg-white/70 rounded-full typing-dot"></div>
      <div className="w-2 h-2 bg-white/70 rounded-full typing-dot"></div>
    </div>
  </div>
));

// Markdown components for styling
const markdownComponents = {
  h1: ({children}) => <h1 className="text-lg sm:text-xl font-bold mb-2 mt-3">{children}</h1>,
  h2: ({children}) => <h2 className="text-base sm:text-lg font-bold mb-2 mt-3">{children}</h2>,
  h3: ({children}) => <h3 className="text-sm sm:text-base font-semibold mb-1 mt-2">{children}</h3>,
  h4: ({children}) => <h4 className="text-sm font-semibold mb-1 mt-2">{children}</h4>,
  p: ({children}) => <p className="mb-2 last:mb-0">{children}</p>,
  ul: ({children}) => <ul className="list-disc list-inside mb-2 space-y-1">{children}</ul>,
  ol: ({children}) => <ol className="list-decimal list-inside mb-2 space-y-1">{children}</ol>,
  li: ({children}) => <li className="text-sm">{children}</li>,
  strong: ({children}) => <strong className="font-semibold">{children}</strong>,
  em: ({children}) => <em className="italic">{children}</em>,
  a: ({href, children}) => (
    <a 
      href={href} 
      target="_blank" 
      rel="noopener noreferrer"
      className="underline inline-flex items-center gap-0.5"
      style={{ color: COLORS.primaryRed }}
    >
      {children}
      <ExternalLink size={10} />
    </a>
  ),
  code: ({children}) => (
    <code className="bg-white/20 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
  ),
  hr: () => <hr className="my-3 border-white/20" />,
  table: ({children}) => (
    <div className="overflow-x-auto my-3 rounded-lg border border-white/20">
      <table className="w-full text-xs sm:text-sm">{children}</table>
    </div>
  ),
  thead: ({children}) => <thead className="bg-white/10 text-left">{children}</thead>,
  tbody: ({children}) => <tbody className="divide-y divide-white/10">{children}</tbody>,
  tr: ({children}) => <tr className="hover:bg-white/5">{children}</tr>,
  th: ({children}) => <th className="px-3 py-2 font-semibold whitespace-nowrap">{children}</th>,
  td: ({children}) => <td className="px-3 py-2">{children}</td>,
};

// Memoized Chat Message
const ChatMessage = memo(({ message, index, isLast, onRate, rating }) => (
  <div
    className={`flex flex-col mb-3 sm:mb-4 ${
      message.role === "user" ? "items-end" : "items-start"
    }`}
  >
    <div className={`flex max-w-[95%] sm:max-w-[85%] ${message.role === "user" ? "justify-end" : "justify-start"}`}>
      {message.role === "assistant" && (
        <MoneyGuroAvatar size={32} className="mr-2 sm:mr-3 border-2 hidden sm:flex" style={{ borderColor: COLORS.primaryRed }} />
      )}
      <div
        className={`rounded-2xl p-3 sm:p-4 ${
          message.role === "user" 
            ? "bg-white text-[#1A1A1A] border border-gray-200 rounded-tr-none" 
            : "text-white rounded-tl-none"
        }`}
        style={message.role === "assistant" ? { backgroundColor: COLORS.primaryDark } : {}}
        data-testid={`chat-message-${index}`}
      >
        <div className="text-sm sm:text-base leading-relaxed">
          {message.role === "assistant" ? (
            <ChatContent
              content={message.content}
              MarkdownComponent={ReactMarkdown}
              markdownProps={{ components: markdownComponents, remarkPlugins: [remarkGfm] }}
            />
          ) : (
            <span className="whitespace-pre-wrap">{message.content}</span>
          )}
        </div>
      </div>
    </div>
    
    {/* Rating + Branch Button row for assistant messages */}
    {message.role === "assistant" && message.message_id && (
      <div className="flex items-center gap-2 mt-1.5 sm:ml-[44px]">
        <div className="flex items-center gap-1 bg-white rounded-full border border-gray-100 shadow-sm px-1 py-0.5" data-testid={`rating-${index}`}>
          <button
            onClick={() => onRate(message.message_id, "up")}
            disabled={!!rating}
            className={`p-1.5 rounded-full transition-all ${
              rating === "up"
                ? "bg-green-100 text-green-600"
                : rating
                ? "text-gray-200 cursor-default"
                : "text-gray-400 hover:text-green-600 hover:bg-green-50"
            }`}
            data-testid={`rate-up-${index}`}
            aria-label="Helpful"
          >
            <ThumbsUp size={14} fill={rating === "up" ? "currentColor" : "none"} />
          </button>
          <button
            onClick={() => onRate(message.message_id, "down")}
            disabled={!!rating}
            className={`p-1.5 rounded-full transition-all ${
              rating === "down"
                ? "bg-red-100 text-red-500"
                : rating
                ? "text-gray-200 cursor-default"
                : "text-gray-400 hover:text-red-500 hover:bg-red-50"
            }`}
            data-testid={`rate-down-${index}`}
            aria-label="Not helpful"
          >
            <ThumbsDown size={14} fill={rating === "down" ? "currentColor" : "none"} />
          </button>
        </div>
        {rating && <span className="text-[10px] text-gray-400">Thanks!</span>}
        
        {/* Find Branch Button - only on last assistant message */}
        {isLast && (
          <a
            href={CEBUANA_LINKS.branchLocator}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 text-white text-xs sm:text-sm font-medium rounded-full transition-all shadow-md hover:opacity-90"
            style={{ background: `linear-gradient(to right, ${COLORS.primaryRed}, #f25f64)` }}
            data-testid="find-branch-btn"
          >
            <MapPin size={14} />
            <span className="hidden sm:inline">Find Nearest Cebuana Branch</span>
            <span className="sm:hidden">Find Branch</span>
            <ExternalLink size={12} />
          </a>
        )}
      </div>
    )}
    
    {/* Find Branch for messages without message_id (error fallbacks) */}
    {message.role === "assistant" && !message.message_id && isLast && (
      <div className="mt-2 sm:ml-[44px]">
        <a
          href={CEBUANA_LINKS.branchLocator}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1.5 px-3 py-1.5 sm:px-4 sm:py-2 text-white text-xs sm:text-sm font-medium rounded-full transition-all shadow-md hover:opacity-90"
          style={{ background: `linear-gradient(to right, ${COLORS.primaryRed}, #f25f64)` }}
          data-testid="find-branch-btn"
        >
          <MapPin size={14} />
          <span className="hidden sm:inline">Find Nearest Cebuana Branch</span>
          <span className="sm:hidden">Find Branch</span>
          <ExternalLink size={12} />
        </a>
      </div>
    )}
  </div>
));

// Quick Start Card Component
const QuickStartCard = memo(({ card, onSelect, userLang }) => {
  const Icon = card.icon;
  const prompt = localize(card.prompt, userLang || "default");
  const title = localize(card.title, userLang || "default");
  const subtitle = localize(card.subtitle, userLang || "default");
  return (
    <button
      onClick={() => onSelect(prompt)}
      className="flex items-center gap-3 w-full p-3 sm:p-4 bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-md hover:border-gray-200 transition-all text-left group"
      data-testid={`quickstart-${card.id}`}
    >
      <div
        className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110"
        style={{ backgroundColor: `${card.color}15` }}
      >
        <Icon size={20} style={{ color: card.color }} className="sm:w-6 sm:h-6" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-semibold text-sm sm:text-base text-[#1A1A1A] truncate">{title}</p>
        <p className="text-xs text-[#888] truncate">{subtitle}</p>
      </div>
      <ArrowRight size={16} className="text-[#ccc] group-hover:text-[#888] flex-shrink-0 transition-colors" />
    </button>
  );
});

// Greeting & farewell messages
const PRIVACY_CONSENT_MSG = {
  role: "assistant",
  content: "Kumusta! Ako si **Money Guro**, ang iyong Filipino Financial Advisor.\n\nBago tayo mag-start, kailangan ko muna ng iyong consent:\n\nBy providing your consent, you agree to the collection, use, processing, and sharing of your personal data by PJ Lhuillier Inc. with its affiliates, partners, and third-party contractors, in accordance with applicable laws and regulations. Your personal data will be protected through appropriate security measures, retained only for as long as necessary, and securely disposed of once the purpose has been fulfilled. You may exercise your rights under the Data Privacy Act of 2012 at any time. To learn more, please review our detailed Privacy Policy by clicking the [link](https://cebuanalhuillier.com/privacy-policy/).",
  isConsent: true,
};

const GREETING_MSG = {
  role: "assistant",
  content: "Salamat sa iyong consent! Ano ang pangalan mo?",
};

const CONSENT_DECLINED_MSG = {
  role: "assistant",
  content: "Naiintindihan namin. Hindi namin mapoproseso ang iyong request nang walang consent. Kung magbago ang isip mo, bumalik ka lang anytime. Salamat!",
};

const makeFarewell = (name, messages) => {
  // Build a personalized farewell with conversation summary
  const topics = messages
    .filter(m => m.role === "user" && m.message_id === undefined)
    .slice(-5)
    .map(m => m.content)
    .filter(c => c.length > 10);
  
  const summary = topics.length > 0
    ? `\n\nKey takeaway natin today: focus on your financial goals, one step at a time.`
    : "";

  return {
    role: "assistant",
    content: `Salamat sa pag-chat${name ? `, **${name}**` : ""}! Sana nakatulong ako sa'yo.${summary}\n\n**Action step mo today:** Start with the first tip we discussed - even small steps count!\n\nKung may tanong ka pa, andito lang si Money Guro. Omsim!`,
  };
};

const makeLangPicker = (name) => ({
  role: "assistant",
  content: `Nice to meet you, **${name}**! What language do you prefer?`,
  isLangPicker: true,
});

const makeWelcome = (name, lang) => ({
  role: "assistant",
  content: lang === "english"
    ? `Great, **${name}**! I'll chat with you in English. What financial concern can I help you with? Pick a topic below or type your own question!`
    : lang === "bisaya"
    ? `Nice, **${name}**! Mag-chat ta in Bisaya. Unsay financial concern nimo? Pili sa topics sa ubos o i-type ang imong pangutana!`
    : lang === "ilokano"
    ? `Nice, **${name}**! Mag-chat tayo in Ilokano. Ania ti financial concern mo? Pumili ka iti topics dita baba wenno i-type mo ti saludsod mo!`
    : lang === "bikolano"
    ? `Nice, **${name}**! Mag-chat kita in Bikolano. Ano an financial concern mo? Pumili sa mga topics sa baba o mag-type kan sadiri mong hapot!`
    : lang === "tagalog"
    ? `Nice to meet you, **${name}**! Mag-uusap tayo sa Tagalog. Ano ang gusto mong pag-usapan? Pumili sa mga topics sa baba o mag-type ng sarili mong tanong!`
    : `Nice to meet you, **${name}**! Handa na akong tumulong sa'yo.\n\nAno ang gusto mong pag-usapan? Pumili sa mga topics below, o mag-type ng sarili mong tanong!`,
});

// Main ChatPage Component
const ChatPage = () => {
  const healthStatus = useHealthCheck();
  const [messages, setMessages] = useState([PRIVACY_CONSENT_MSG]);
  const [inputValue, setInputValue] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [quickPrompts, setQuickPrompts] = useState([]);
  const [contextPrompts, setContextPrompts] = useState([]);
  const [userName, setUserName] = useState(null);
  const [userLang, setUserLang] = useState(null);
  const [awaitingConsent, setAwaitingConsent] = useState(true);
  const [awaitingName, setAwaitingName] = useState(false);
  const [awaitingLang, setAwaitingLang] = useState(false);
  const [showTopics, setShowTopics] = useState(false);
  const [chatEnded, setChatEnded] = useState(false);
  const [ratings, setRatings] = useState({});
  const [showSurvey, setShowSurvey] = useState(false);
  const [surveySubmitted, setSurveySubmitted] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Restore session from secure transfer token (from embed maximize)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const token = params.get("t");
    if (token) {
      // Clean URL immediately to prevent token reuse from browser history
      window.history.replaceState({}, "", window.location.pathname);
      axios.get(`${API}/chat/restore/${token}`).then(res => {
        const { session_id, user_name, messages: history } = res.data;
        setUserName(user_name);
        setAwaitingName(false);
        setSessionId(session_id);
        const restored = [GREETING_MSG, { role: "user", content: user_name }, makeWelcome(user_name)];
        for (const m of history) {
          restored.push({ role: "user", content: m.user_message });
          restored.push({ role: "assistant", content: m.assistant_response, message_id: m.message_id });
        }
        setMessages(restored);
      }).catch(() => {
        toast.error("Session transfer expired or invalid. Starting fresh.");
      });
    }
  }, []);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [messages, isLoading]);

  // Fetch quick prompts
  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await axios.get(`${API}/quick-prompts`);
        setQuickPrompts(res.data.prompts);
      } catch (error) {
        console.error("Error fetching prompts:", error);
      }
    };
    fetchData();
  }, []);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = "auto";
      textareaRef.current.style.height = Math.min(textareaRef.current.scrollHeight, 120) + "px";
    }
  }, [inputValue]);

  const sendMessage = async (messageText, { skipAutoDetect = false } = {}) => {
    if (!messageText.trim() || isLoading || chatEnded || awaitingConsent) return;

    const userMessage = {
      role: "user",
      content: messageText,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue("");
    setContextPrompts([]);

    // Name capture — first message is treated as the user's name
    if (awaitingName) {
      const name = messageText.trim().split(/\s+/).slice(0, 3).join(" ");
      setUserName(name);
      setAwaitingName(false);
      setAwaitingLang(true);
      setMessages(prev => [...prev, makeLangPicker(name)]);
      return;
    }

    setIsLoading(true);
    setShowTopics(false);

    try {
      const response = await axios.post(`${API}/chat`, {
        message: messageText,
        session_id: sessionId,
        user_name: userName,
        language: userLang,
      });

      setSessionId(response.data.session_id);

      // Auto-detect language switch — only for user-typed messages, not card/suggestion clicks
      if (!skipAutoDetect) {
        const detectedLang = response.data.detected_language;
        if (detectedLang && detectedLang !== userLang) {
          const langNames = { bisaya: "Bisaya", ilokano: "Ilokano", bikolano: "Bikolano", tagalog: "Tagalog", english: "English", taglish: "Taglish" };
          const langName = langNames[detectedLang] || detectedLang;
          setUserLang(detectedLang);
          toast(`Nag-switch tayo sa ${langName}!`, { icon: "🇵🇭", duration: 3000 });
        }
      }

      const botMessage = {
        role: "assistant",
        content: response.data.response,
        message_id: response.data.message_id,
        timestamp: new Date(),
      };

      setMessages(prev => [...prev, botMessage]);

      // If chat was force-ended by the system (persistent off-topic or security termination)
      if (response.data.chat_ended || response.data.source === "system") {
        setChatEnded(true);
        setContextPrompts([]);
        if (!surveySubmitted) setTimeout(() => setShowSurvey(true), 1200);
        return;
      }

      // Fetch context-aware suggestions in the background (skip for off-topic)
      if (response.data.source !== "off_topic") {
        axios.post(`${API}/chat/suggestions`, {
          session_id: response.data.session_id,
          last_bot_response: response.data.response,
        }).then(res => {
          if (res.data.suggestions?.length) setContextPrompts(res.data.suggestions);
        }).catch(() => {});
      }
    } catch (error) {
      console.error("Error sending message:", error);
      const status = error?.response?.status;
      const detail = error?.response?.data?.detail;
      let toastMsg = "May problema sa connection. Subukan ulit!";
      let chatMsg = "Pasensya na, may technical difficulty tayo ngayon. Subukan mo ulit mamaya!";
      if (status === 504 || detail?.includes("too long")) {
        toastMsg = "Medyo matagal ang response. Subukan ulit!";
        chatMsg = "Pasensya na, medyo matagal ang response ngayon. Subukan mo ulit, lods!";
      } else if (status === 429) {
        toastMsg = "Maraming requests ngayon. Sandali lang!";
        chatMsg = "Medyo busy tayo ngayon, lods. Subukan mo ulit in a few seconds!";
      }
      toast.error(toastMsg);
      
      setMessages(prev => [...prev, {
        role: "assistant",
        content: chatMsg,
        timestamp: new Date(),
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    // Enter sends the message, Shift+Enter for new line
    if (e.key === "Enter" && !e.shiftKey && !e.ctrlKey) {
      e.preventDefault();
      sendMessage(inputValue);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    sendMessage(inputValue);
  };

  const handleConsent = (accepted) => {
    setAwaitingConsent(false);
    if (accepted) {
      setAwaitingName(true);
      setMessages(prev => [
        ...prev,
        { role: "user", content: "I agree" },
        GREETING_MSG,
      ]);
    } else {
      setChatEnded(true);
      setMessages(prev => [
        ...prev,
        { role: "user", content: "I decline" },
        CONSENT_DECLINED_MSG,
      ]);
    }
  };

  const handleLangSelect = (langId) => {
    setUserLang(langId);
    setAwaitingLang(false);
    const langLabel = LANGUAGE_OPTIONS.find(l => l.id === langId)?.label || langId;
    setMessages(prev => [
      ...prev,
      { role: "user", content: langLabel },
      makeWelcome(userName, langId),
    ]);
  };

  const handleBackToTopics = () => {
    setShowTopics(true);
    setContextPrompts([]);
    // Scroll to bottom after topics are shown
    setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const handleNewChat = () => {
    // Show survey if there was a real conversation and survey not yet submitted
    if (sessionId && messages.length > 2 && !surveySubmitted) {
      setPendingAction("new_chat");
      setShowSurvey(true);
      return;
    }
    resetChat();
  };

  const handleEndChat = () => {
    setChatEnded(true);
    setMessages(prev => [...prev, makeFarewell(userName, prev)]);
    if (sessionId && messages.length > 2 && !surveySubmitted) {
      setTimeout(() => setShowSurvey(true), 800);
    }
  };

  const resetChat = () => {
    setMessages([PRIVACY_CONSENT_MSG]);
    setSessionId(null);
    setRatings({});
    setUserName(null);
    setUserLang(null);
    setAwaitingConsent(true);
    setAwaitingName(false);
    setAwaitingLang(false);
    setShowTopics(false);
    setChatEnded(false);
    setContextPrompts([]);
    setShowSurvey(false);
    setSurveySubmitted(false);
    setPendingAction(null);
  };

  const handleSurveyClose = () => {
    setShowSurvey(false);
    if (pendingAction === "new_chat") resetChat();
    setPendingAction(null);
  };

  const handleSurveySubmitted = () => {
    setSurveySubmitted(true);
    setShowSurvey(false);
    if (pendingAction === "new_chat") resetChat();
    setPendingAction(null);
  };

  const handleRate = async (messageId, rating) => {
    if (ratings[messageId]) return;
    setRatings(prev => ({ ...prev, [messageId]: rating }));
    try {
      await axios.post(`${API}/chat/rate`, { message_id: messageId, rating });
    } catch {
      // Silent fail — rating already stored in UI state
    }
  };

  const exportChatHistory = () => {
    if (messages.length <= 1) { toast.error("No conversation to export yet"); return; }
    const lines = [`# Money Guro Chat - ${new Date().toLocaleDateString()}\n`];
    for (const msg of messages) {
      if (msg.role === "user") lines.push(`**You:** ${msg.content}\n`);
      else if (msg.role === "assistant") lines.push(`**Money Guro:** ${msg.content}\n`);
    }
    lines.push(`\n---\n*Exported from Money Guro by Cebuana Lhuillier*`);
    const blob = new Blob([lines.join("\n")], { type: "text/markdown" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `money-guro-chat-${new Date().toISOString().slice(0, 10)}.md`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success("Chat exported!");
  };

  // Whether to show quick-start cards (after language selected, before first API message, or when back-to-topics)
  const hasApiMessages = messages.some(m => m.message_id);
  const showQuickCards = !awaitingConsent && !awaitingName && !awaitingLang && !chatEnded && (!hasApiMessages || showTopics);

  return (
    <>
    <div className="h-screen flex flex-col" style={{ backgroundColor: COLORS.secondaryLight }} data-testid="chat-page">
      {/* Header */}
      <header className="bg-white/90 backdrop-blur-sm border-b border-gray-100 px-3 sm:px-4 py-2 sm:py-3 sticky top-0 z-10 flex-shrink-0">
        <div className="max-w-2xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2 sm:gap-3">
            <img src="/moneyguro-logo.png" alt="Money Guro" className="h-9 sm:h-10 w-auto object-contain" data-testid="header-logo" />
            <div className="flex items-center gap-1.5">
              <p className="text-[10px] sm:text-xs text-[#666666]">
                Your Filipino Financial Advisor
              </p>
              <span
                className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                  healthStatus === "online" ? "bg-emerald-400" : healthStatus === "offline" ? "bg-red-400 animate-pulse" : "bg-gray-300"
                }`}
                title={healthStatus === "online" ? "Connected" : healthStatus === "offline" ? "Connection issue" : "Checking..."}
                data-testid="health-indicator"
              />
            </div>
          </div>
          {!awaitingName && (
            <div className="flex items-center gap-1.5">
              {messages.some(m => m.message_id) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={exportChatHistory}
                  className="text-xs sm:text-sm px-2 hover:bg-blue-50"
                  style={{ color: COLORS.secondaryBlue }}
                  data-testid="export-chat-btn"
                  title="Export conversation"
                >
                  <Download size={14} />
                </Button>
              )}
              {!chatEnded && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleEndChat}
                  className="text-xs sm:text-sm px-2 sm:px-3 hover:bg-red-50"
                  style={{ color: COLORS.primaryRed }}
                  data-testid="end-chat-btn"
                >
                  <LogOut size={14} className="mr-1" />
                  End Chat
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                onClick={handleNewChat}
                className="text-xs sm:text-sm px-2 sm:px-3 hover:bg-gray-100"
                style={{ color: COLORS.primaryDark }}
                data-testid="new-chat-btn"
              >
                <MessageSquarePlus size={14} className="mr-1" />
                New Chat
              </Button>
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 overflow-y-auto">
        <div className="max-w-2xl mx-auto px-3 sm:px-4 py-4">
          {messages.map((message, index) => (
            <div key={index}>
              <ChatMessage 
                message={message} 
                index={index}
                isLast={index === messages.length - 1 && !chatEnded}
                onRate={handleRate}
                rating={message.message_id ? ratings[message.message_id] : null}
              />
              {/* Privacy Consent Buttons — shown inline after the consent message */}
              {message.isConsent && awaitingConsent && (
                <div className="flex gap-3 ml-12 mt-3 mb-4" data-testid="consent-picker">
                  <button
                    onClick={() => handleConsent(true)}
                    className="flex items-center gap-1.5 px-5 py-2 text-sm font-semibold rounded-full transition-all hover:scale-105 text-white"
                    style={{ backgroundColor: COLORS.primaryDark }}
                    data-testid="consent-yes"
                  >
                    Yes, I agree
                  </button>
                  <button
                    onClick={() => handleConsent(false)}
                    className="flex items-center gap-1.5 px-5 py-2 text-sm font-semibold rounded-full border-2 transition-all hover:scale-105"
                    style={{ borderColor: COLORS.primaryRed, color: COLORS.primaryRed }}
                    data-testid="consent-no"
                  >
                    No, I decline
                  </button>
                </div>
              )}
              {/* Language Picker — shown inline after the lang picker message */}
              {message.isLangPicker && awaitingLang && (
                <div className="flex flex-wrap gap-2 ml-12 mt-2 mb-4" data-testid="lang-picker">
                  {LANGUAGE_OPTIONS.map((lang) => (
                    <button
                      key={lang.id}
                      onClick={() => handleLangSelect(lang.id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs sm:text-sm rounded-full border-2 transition-all hover:scale-105"
                      style={{ borderColor: COLORS.primaryDark, color: COLORS.primaryDark, backgroundColor: `${COLORS.primaryDark}08` }}
                      data-testid={`lang-${lang.id}`}
                    >
                      <Globe size={12} />
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Quick Start Cards — shown after language selected, before first real question or on back-to-topics */}
          {showQuickCards && (
            <div className="w-full max-w-md mx-auto space-y-2 sm:space-y-3 my-4" data-testid="quickstart-cards">
              {QUICK_START_CARDS.map((card) => (
                <QuickStartCard key={card.id} card={card} userLang={userLang} onSelect={(prompt) => { setShowTopics(false); sendMessage(prompt, { skipAutoDetect: true }); }} />
              ))}
            </div>
          )}

          {/* End-of-chat CTA */}
          {chatEnded && (
            <div className="flex justify-center mt-4 mb-2">
              <Button
                onClick={handleNewChat}
                className="text-white rounded-full px-6 py-2 text-sm font-medium shadow-md"
                style={{ backgroundColor: COLORS.primaryDark }}
                data-testid="restart-chat-btn"
              >
                <MessageSquarePlus size={16} className="mr-2" />
                Start New Chat
              </Button>
            </div>
          )}

          {isLoading && <TypingIndicator />}
          <div ref={messagesEndRef} />
        </div>
      </main>

      {/* Input Area */}
      <footer className="border-t border-gray-100 px-3 sm:px-4 py-3 sm:py-4 flex-shrink-0" style={{ backgroundColor: COLORS.secondaryLight }}>
        <div className="max-w-2xl mx-auto">
          {/* Quick prompts + Smart chart action + Common shortcuts */}
          {!awaitingName && !awaitingLang && !chatEnded && userName && hasApiMessages && !showTopics && (() => {
            const lastBot = [...messages].reverse().find(m => m.role === "assistant" && m.message_id);
            const hasNumbers = lastBot && /(?:PHP\s*[\d,]+|[\d,]+(?:\.\d+)?%|\d{1,3}(?:,\d{3})+)/gi.test(lastBot.content);
            const hasChart = lastBot && /```chart:/i.test(lastBot.content);
            const showChartAction = hasNumbers && !hasChart;
            const prompts = contextPrompts.length > 0 ? contextPrompts : COMMON_SHORTCUTS.slice(0, 3);
            return (
              <div className="space-y-2 mb-2">
                <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
                  {showChartAction && (
                    <button
                      className="quick-prompt-btn whitespace-nowrap text-[10px] sm:text-xs flex items-center gap-1 border-dashed"
                      style={{ borderColor: COLORS.secondaryBlue, color: COLORS.secondaryBlue }}
                      onClick={() => sendMessage("Can you visualize that as a pie chart?", { skipAutoDetect: true })}
                      data-testid="chart-action-btn"
                    >
                      <TrendingUp size={10} />
                      Visualize as chart
                    </button>
                  )}
                  {prompts.map((prompt, index) => (
                    <button
                      key={`${prompt}-${index}`}
                      className="quick-prompt-btn whitespace-nowrap text-[10px] sm:text-xs flex items-center gap-1"
                      onClick={() => sendMessage(prompt, { skipAutoDetect: true })}
                      data-testid={`context-prompt-${index}`}
                    >
                      <ArrowRight size={10} style={{ color: COLORS.primaryDark }} />
                      {prompt}
                    </button>
                  ))}
                </div>
                {/* Back to Topics button */}
                <div className="flex justify-center">
                  <button
                    onClick={handleBackToTopics}
                    className="flex items-center gap-1 text-[10px] sm:text-xs px-3 py-1 rounded-full transition-colors hover:bg-gray-100"
                    style={{ color: COLORS.secondaryBlue }}
                    data-testid="back-to-topics-btn"
                  >
                    <RotateCcw size={10} />
                    Back to Topics
                  </button>
                </div>
              </div>
            );
          })()}
          
          {/* Input Form — Textarea for multi-line */}
          {chatEnded ? (
            <div className="text-center py-2">
              <p className="text-xs text-[#999]">Chat ended. Click "New Chat" or "Start New Chat" to begin again.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="flex items-end gap-2 bg-white rounded-2xl shadow-lg p-1.5 sm:p-2">
              <textarea
                ref={textareaRef}
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={awaitingConsent ? "Please select an option above..." : awaitingName ? "Type your name..." : awaitingLang ? "Or type your preferred language..." : "Magtanong kay Money Guro..."}
                className="flex-1 bg-transparent border-none outline-none px-3 sm:px-4 py-2 text-sm sm:text-base text-[#1A1A1A] placeholder-gray-400 resize-none"
                disabled={isLoading}
                rows={1}
                style={{ maxHeight: 120, minHeight: 36 }}
                data-testid="chat-input"
              />
              <div className="flex flex-col items-center gap-0.5 flex-shrink-0">
                <Button
                  type="submit"
                  disabled={!inputValue.trim() || isLoading}
                  className="text-white rounded-full px-3 sm:px-4 py-2 text-sm font-medium disabled:opacity-50"
                  style={{ backgroundColor: COLORS.primaryDark }}
                  data-testid="send-btn"
                >
                  <Send size={16} className="sm:mr-1" />
                  <span className="hidden sm:inline">Send</span>
                </Button>
                <span className="text-[9px] text-[#bbb] hidden sm:block">Enter</span>
              </div>
            </form>
          )}
          
          {/* Footer Links */}
          <div className="flex flex-wrap justify-center gap-1.5 sm:gap-2 mt-2 sm:mt-3">
            <a
              href={CEBUANA_LINKS.branchLocator}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-medium rounded-full transition-colors"
              style={{ backgroundColor: `${COLORS.primaryDark}15`, color: COLORS.primaryDark }}
            >
              <MapPin size={10} />
              Find Branch
            </a>
            <a
              href={CEBUANA_LINKS.kanegosyo}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-medium rounded-full transition-colors"
              style={{ backgroundColor: `${COLORS.primaryRed}15`, color: COLORS.primaryRed }}
            >
              <Store size={10} />
              Kanegosyo Center
            </a>
            <a
              href={CEBUANA_LINKS.microSavings}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2 sm:px-3 py-1 text-[10px] sm:text-xs font-medium rounded-full transition-colors"
              style={{ backgroundColor: `${COLORS.secondaryBlue}15`, color: COLORS.secondaryBlue }}
            >
              <PiggyBank size={10} />
              Savings
            </a>
          </div>
          
          <p className="text-center text-[10px] sm:text-xs text-[#666666] mt-2">
            Powered by <a href={CEBUANA_LINKS.kanegosyo} target="_blank" rel="noopener noreferrer" style={{ color: COLORS.primaryDark }} className="hover:underline">Kanegosyo Center</a> • <a href="https://www.cebuanalhuillier.com" target="_blank" rel="noopener noreferrer" style={{ color: COLORS.primaryRed }} className="hover:underline">Cebuana Lhuillier</a>
          </p>
        </div>
      </footer>
    </div>

    {showSurvey && (
      <SurveyModal
        sessionId={sessionId}
        userName={userName}
        onClose={handleSurveyClose}
        onSubmitted={handleSurveySubmitted}
      />
    )}
    </>
  );
};

export default ChatPage;
